import { ApiService } from 'services/Api';
import {
    AttendanceMetrics,
    AttendanceMetricsResponse,
    MetricsValues,
    RequestBody,
    TagResponse,
    TicketsResponse,
} from 'typings';
import { AgentResponse } from 'typings/services/AgentsResponse';

export class ReportService {
    private readonly apiService: ApiService;
    private readonly tenant: string;

    constructor(baseUrl: string, token: string, tenant: string) {
        this.apiService = new ApiService(baseUrl, '', '', token);
        this.tenant = tenant;
    }

    timeMetricsToObj(timeMetrics: MetricsValues[]): { [key: string]: MetricsValues } {
        return timeMetrics.reduce((object, timeMetric) => {
            return { ...object, [timeMetric.description]: timeMetric };
        }, {});
    }

    countAttendantsByStatus(attendants: AgentResponse['attendants']): { online: number; invisible: number; pause: number } {
        const seen = new Set<string>();
        const counts = { online: 0, invisible: 0, pause: 0 };

        for (const { name, status } of attendants) {
            if (!seen.has(name)) {
                seen.add(name);
                const normalized = status.toLowerCase() as keyof typeof counts;
                if (normalized in counts) counts[normalized]++;
            }
        }

        return counts;
    }

    async getAttendantStatusCounts(bots: string[]): Promise<{ online: number; invisible: number; pause: number }> {
        const agentResponse = await this.getAgents(bots);
        return this.countAttendantsByStatus(agentResponse.attendants);
    }

    async getAttendanceMetrics(bots: string[]): Promise<AttendanceMetrics> {
        try {
            const body: RequestBody = { tenant: this.tenant, bots };
            const response = await this.apiService.post<AttendanceMetricsResponse>(
                'v1/attendance-metrics/status',
                body,
            );
            const attendanceMetrics: AttendanceMetrics = {
                tickets: {
                    ...response.tickets,
                    waiting: {
                        amount: response.tickets.waiting.amount,
                        timeMetrics: this.timeMetricsToObj(
                            response.tickets.waiting.timeMetrics,
                        ) as AttendanceMetrics['tickets']['waiting']['timeMetrics'],
                    },
                    inAttendance: {
                        ...response.tickets.inAttendance,
                        timeMetrics: this.timeMetricsToObj(
                            response.tickets.inAttendance.timeMetrics,
                        ) as AttendanceMetrics['tickets']['inAttendance']['timeMetrics'],
                    },
                },
                attendants: { online: { amount: 0 }, invisible: { amount: 0 }, pause: { amount: 0 }, ticketsPerAttendant: { amount: 0 } },
                bots: response.bots,
            };
            return attendanceMetrics;
        } catch (error) {
            throw error;
        }
    }

    async getTicketsInAttendance(bots: string[], pageNumber: number, pageSize: number): Promise<TicketsResponse> {
        try {
            const body: RequestBody = {
                tenant: this.tenant,
                bots,
                pagination: { pageNumber, pageSize },
                status: 'IN_ATTENDANCE',
            };
            const response = await this.apiService.post<TicketsResponse>('v1/attendance-metrics/tickets', body);
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getTicketInQueue(bots: string[], pageNumber: number, pageSize: number): Promise<TicketsResponse> {
        try {
            const body: RequestBody = {
                tenant: this.tenant,
                bots,
                pagination: { pageNumber, pageSize },
                status: 'WAITING',
            };
            const response = await this.apiService.post<TicketsResponse>('v1/attendance-metrics/tickets', body);
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getAgents(bots: string[]): Promise<AgentResponse> {
        try {
            const body: RequestBody = {
                tenant: this.tenant,
                bots,
                pagination: {
                    pageNumber: 1,
                    pageSize: 999999,
                },
            };
            const response = await this.apiService.post<AgentResponse>('/v1/attendance-metrics/attendants', body);
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getTags(bots: string[]): Promise<TagResponse> {
        try {
            const body: RequestBody = {
                tenant: this.tenant,
                bots,
                pagination: {
                    pageNumber: 1,
                    pageSize: 999999,
                },
            };
            const response = await this.apiService.post<TagResponse>('/v1/attendance-metrics/tags', body);
            return response;
        } catch (error) {
            throw error;
        }
    }
}
